import React from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  StyleSheet,
  Platform,
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import PixelContainer from './PixelContainer';
import { fontStyle } from '../config/fonts';

interface ConfirmDialogProps {
  visible: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmDestructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  visible,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmDestructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { colors } = useTheme();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <Pressable style={styles.overlay} onPress={onCancel}>
        <Pressable onPress={(e) => e.stopPropagation()}>
          <PixelContainer
            backgroundColor={colors.background}
            cornerSize={6}
            style={styles.dialog}
          >
            <View style={styles.content}>
              <Text style={[styles.title, fontStyle, { color: colors.text }]}>
                {title}
              </Text>
              <Text style={[styles.message, fontStyle, { color: colors.textSecondary }]}>
                {message}
              </Text>
              <View style={styles.buttons}>
                <Pressable onPress={onCancel} style={styles.buttonWrapper}>
                  <PixelContainer
                    backgroundColor={colors.surface}
                    cornerSize={4}
                    style={styles.button}
                  >
                    <Text style={[styles.buttonText, fontStyle, { color: colors.text }]}>
                      {cancelText}
                    </Text>
                  </PixelContainer>
                </Pressable>
                <Pressable onPress={onConfirm} style={styles.buttonWrapper}>
                  <PixelContainer
                    backgroundColor={confirmDestructive ? colors.error : colors.primary}
                    cornerSize={4}
                    style={styles.button}
                  >
                    <Text style={[styles.buttonText, fontStyle, { color: '#FFFFFF' }]}>
                      {confirmText}
                    </Text>
                  </PixelContainer>
                </Pressable>
              </View>
            </View>
          </PixelContainer>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  dialog: {
    width: '100%',
    maxWidth: 340,
    padding: 24,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    }),
  },
  content: {
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 24,
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
  },
  buttonWrapper: {
    flex: 1,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
